import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { IMAGES, STATS } from "@/data/site";
import { lineParent, lineChild } from "@/lib/motionPrimitives";
import Counter from "./Counter";

const LINES = ["All Industrial Paint", "Coatings, Retrofitting", "And Civil Work Solutions"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Industrial steel structure"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />

      <div className="container-x relative z-10 flex h-full flex-col justify-end pb-16 md:pb-20">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-white/80 font-display"
        >
          <span className="h-px w-10 bg-primary" /> We Protect Your Assets
        </motion.p>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight text-white max-w-5xl"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineChild} className="block">
                {i === 2 ? (
                  <>
                    And Civil Work <span className="text-primary">Solutions</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 max-w-xl text-base md:text-lg text-white/85"
        >
          Protecting Structures. Enhancing Performance. Extending Asset Life.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a href="#contact" className="btn-primary" data-testid="hero-quote-btn">
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href="#services"
            className="btn-outline border-white/40 text-white hover:border-white hover:text-white"
            data-testid="hero-services-btn"
          >
            Our Services
          </a>
        </motion.div>
      </div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.7 }}
        className="absolute inset-x-0 bottom-0 z-10 hidden md:block"
      >
        <div className="container-x">
          <div className="glass grid grid-cols-4 divide-x divide-white/15 rounded-t-2xl border border-b-0 border-white/15 py-6">
            {STATS.map((s) => (
              <div key={s.label} className="px-6 text-center text-white" data-testid={`hero-stat-${s.label}`}>
                <div className="font-display text-3xl lg:text-4xl font-black text-primary-foreground">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-1 text-xs uppercase tracking-wider text-white/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="absolute bottom-24 right-6 z-10 hidden animate-bounce text-white/70 lg:block">
        <ArrowDown className="h-6 w-6" />
      </div>
    </section>
  );
}
